import React, { useCallback, useEffect, useState } from "react";

import { Button, Icon, Modal } from "semantic-ui-react";

import CONFIG from "config/config";

import ModConfirm from "react-lib/apps/common/cnmi/ModConfirm";

import { MeLabReportSummary, RunSequence } from "react-lib/apps/HISV3/LAB/sequence/LabReport";

// ======================= Constants =======================
const APPROVE_KEY = "ModMeLabVideoPreview_APPROVE";

// ======================= Types =======================
type ModMeLabVideoPreviewProps = {
  open: boolean;
  card: string;
  video: MeLabReportSummary | null;
  runSequence: RunSequence;
  buttonLoadCheck?: Record<string, any>;
  onClose: () => void;
  onRegenerate: () => void;
  onRegenerateFromLibrary?: () => void;
  // มีเฉพาะ report ที่ v5 สร้าง project ไว้ให้ที่ MeGen
  onGotoMeGen?: () => void;
};

// ปิด config ต้องเป็น children ตรง ๆ ของ Modal.Actions ไม่งั้นการจัดวางเดิมของ semantic เพี้ยน
const ActionGroup = (props: { enabled: boolean; children: React.ReactNode }) =>
  props.enabled ? (
    <div
      style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", justifyContent: "flex-end" }}
    >
      {props.children}
    </div>
  ) : (
    <>{props.children}</>
  );

// ======================= Component =======================
const ModMeLabVideoPreview = (props: ModMeLabVideoPreviewProps) => {
  const [videoUrl, setVideoUrl] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);

  const isApproved = !!props.video?.approved_at;
  // ปิด config = โมดัลกลับไปเป็นแบบเดิม (ปิด / สร้างใหม่ / อนุมัติ) ไม่มีของฝั่ง MeGen
  const showLibraryAction = CONFIG.ENABLE_MELAB_VIDEO_V5;
  const actionProps = showLibraryAction ? { size: "small" as const, style: { margin: 0 } } : {};

  // Fetch download URL when modal opens
  useEffect(() => {
    if (!props.open || !props.video?.report_id) {
      setVideoUrl("");
      setError("");
      return;
    }

    setLoading(true);
    setError("");

    props.runSequence({
      sequence: "LabReport",
      action: "FETCH_MELAB_VIDEO_DOWNLOAD",
      reportId: props.video.report_id,
      onSuccess: (url: string) => {
        setVideoUrl(url);
        setLoading(false);
      },
      onError: (err: any) => {
        setError(typeof err === "string" ? err : "ดึง URL ไม่สำเร็จ");
        setLoading(false);
      },
    });
  }, [props.open, props.video?.report_id]);

  const handleApprove = useCallback(() => {
    if (!props.video?.report_id) return;

    props.runSequence({
      sequence: "LabReport",
      action: "APPROVE_MELAB_VIDEO",
      card: props.card,
      buttonLoadKey: APPROVE_KEY,
      reportId: props.video.report_id,
      onSuccess: () => {
        setOpenConfirm(false);
        props.onClose();
      },
    });
  }, [props.card, props.video?.report_id]);

  return (
    <Modal className="centered" open={props.open} onClose={props.onClose} size="small" closeIcon>
      <Modal.Header>{props.video?.report_name || "Video อธิบายผล"}</Modal.Header>
      <Modal.Content>
        {loading ? (
          <div style={{ textAlign: "center", padding: "2rem" }}>กำลังโหลด...</div>
        ) : error ? (
          <div style={{ color: "red", textAlign: "center", padding: "2rem" }}>{error}</div>
        ) : videoUrl ? (
          <video
            src={videoUrl}
            controls
            autoPlay={false}
            style={{ width: "100%", maxHeight: "60vh" }}
          />
        ) : (
          <div style={{ textAlign: "center", padding: "2rem" }}>ไม่พบ Video</div>
        )}
      </Modal.Content>
      <Modal.Actions>
        {/* config เปิด = ปุ่มเยอะจนตกบรรทัด ต้องจัด flex + ย่อขนาด · ปิด = ปล่อยตามเดิมของ Modal.Actions */}
        <ActionGroup enabled={showLibraryAction}>
          <Button {...actionProps} onClick={props.onClose}>
            ปิด
          </Button>

          <Button {...actionProps} color="teal" onClick={props.onRegenerate}>
            {showLibraryAction ? "สร้างใหม่ (ปกติ)" : "สร้างใหม่"}
          </Button>

          {showLibraryAction && !!props.onRegenerateFromLibrary && (
            <Button {...actionProps} color="violet" onClick={props.onRegenerateFromLibrary}>
              สร้างใหม่ (จากคลัง)
            </Button>
          )}

          {showLibraryAction && !!props.onGotoMeGen && (
            <Button {...actionProps} color="purple" onClick={props.onGotoMeGen}>
              <Icon name="external" />
              แก้ไขใน MeGen
            </Button>
          )}

          {!isApproved && (
            <Button
              {...actionProps}
              color="green"
              disabled={!videoUrl || !!error}
              onClick={() => setOpenConfirm(true)}
            >
              อนุมัติให้คนไข้ดู
            </Button>
          )}
        </ActionGroup>
      </Modal.Actions>

      <ModConfirm
        openModal={openConfirm}
        centered
        titleName="ยืนยันการอนุมัติ"
        size="mini"
        approveButtonText="อนุมัติ"
        denyButtonText="ยกเลิก"
        content={<div style={{ margin: "0.5rem 0" }}>อนุมัติ Video ให้คนไข้ดูใช่หรือไม่</div>}
        approveButtonLoading={props.buttonLoadCheck?.[APPROVE_KEY] === "LOADING"}
        onApprove={handleApprove}
        onDeny={() => setOpenConfirm(false)}
        onCloseWithDimmerClick={() => setOpenConfirm(false)}
      />
    </Modal>
  );
};

ModMeLabVideoPreview.displayName = "ModMeLabVideoPreview";

export default React.memo(ModMeLabVideoPreview);
